import { FieldType } from "../../type-system/FieldType";
import { shopifyCountryCodeEnumOptions } from "./constants";
import {
  ShopifyFulfillmentOrderDestinationKey,
  ShopifyFulfillmentOrderDestinationToFulfillmentOrderKey,
  ShopifyFulfillmentOrderDestinationToLocationKey,
  ShopifyFulfillmentOrderDestinationToShopKey,
  ShopifyFulfillmentOrderKey,
  ShopifyLocationKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyFulfillmentOrderDestinationKey,
  "2025-04": {
    name: "Shopify Fulfillment Order Destination",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id", shopifyFieldKey: "id" } },
      },
      {
        name: "Address 1",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "address1", shopifyFieldKey: "address1" } },
      },
      {
        name: "Address 2",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "address2", shopifyFieldKey: "address2" } },
      },
      {
        name: "City",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "city", shopifyFieldKey: "city" } },
      },
      {
        name: "Company",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "company", shopifyFieldKey: "company" } },
      },
      {
        name: "Country Code",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "countryCode", shopifyFieldKey: "countryCode", isShopifyEnum: true },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: shopifyCountryCodeEnumOptions,
        },
      },
      {
        name: "Email",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "email", shopifyFieldKey: "email" } },
      },
      {
        name: "First Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "firstName", shopifyFieldKey: "firstName" } },
      },
      {
        name: "Last Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "lastName", shopifyFieldKey: "lastName" } },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderDestinationToLocationKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "locationId", shopifyFieldKey: "locationId" } },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Phone",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "phone", shopifyFieldKey: "phone" } },
      },
      {
        name: "Province",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "province", shopifyFieldKey: "province" } },
      },
      {
        name: "Zip",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "zip", shopifyFieldKey: "zip" } },
      },
      {
        name: "Fulfillment Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderDestinationToFulfillmentOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "fulfillmentOrderId",
            shopifyFieldKey: "fulfillmentOrderId",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderDestinationToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    // synced as part of ShopifyFulfillmentOrder
    syncConfig: { type: "inherit" } as const,
  },
};

export const ShopifyFulfillmentOrderDestination = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
